import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  ChevronsUpDown,
  FileText,
  LayoutDashboard,
  LogOut,
  PenLine,
  Scale,
  ScrollText,
  Settings,
  ShieldCheck,
  Upload,
  UserRound,
  Users,
  BrainCircuit,
} from "lucide-react";
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationSettings } from "@/hooks/useSettings";
import {
  isAdminRole,
  roleLabels,
  type BackendUserRole,
} from "@/lib/permissions";

const workspaceNav = [
  { title: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { title: "Contracts", href: "/contracts", icon: FileText },
  { title: "Contract Upload", href: "/upload", icon: Upload },
  {
    title: "Organization Brain",
    href: "/organization-brain",
    icon: BrainCircuit,
  },
  { title: "Legal Assistant", href: "/legal-assistant", icon: Scale },
];

// requiresAdmin marks items the backend actually 403s for non-Owner/Admin
// roles (Domain & Business Rules, Section 10, for Audit Logging) — keep this
// in sync with what each route actually enforces, not just cosmetic hiding.
const adminNav = [
  { title: "User Management", href: "/users", icon: Users },
  {
    title: "Witness Workflow",
    href: "/witness",
    icon: PenLine,
    requiresAdmin: true,
  },
  {
    title: "Audit Logging",
    href: "/audit",
    icon: ScrollText,
    requiresAdmin: true,
  },
  { title: "Settings", href: "/settings", icon: Settings },
];

function getInitials(name?: string) {
  if (!name) {
    return "AW";
  }

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export function Sidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data: organizationSettings } = useOrganizationSettings();
  const [logoFailedToLoad, setLogoFailedToLoad] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const userName = user?.fullName ?? "Alex Whitfield";
  const roleLabel = user
    ? (roleLabels[user.role as BackendUserRole] ?? "Team member")
    : "Team member";
  const visibleAdminNav = adminNav.filter(
    (item) => !item.requiresAdmin || isAdminRole(user?.role),
  );

  async function handleLogout() {
    try {
      setIsLoggingOut(true);
      await logout();
      navigate("/login", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  }

  const organizationName = organizationSettings?.organization.name ?? "Clausio";
  const logoUrl = organizationSettings?.settings.logoUrl;
  const showLogo = Boolean(logoUrl) && !logoFailedToLoad;

  return (
    <SidebarRoot collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="data-open:bg-sidebar-accent"
              render={
                <Link to="/settings?section=organization">
                  <div className="flex aspect-square size-8 items-center justify-center overflow-hidden rounded-lg border bg-muted">
                    {showLogo ? (
                      <img
                        src={logoUrl!}
                        alt=""
                        className="size-full object-cover"
                        onError={() => setLogoFailedToLoad(true)}
                      />
                    ) : (
                      <Building2 className="size-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {organizationName}
                    </span>
                  </div>
                </Link>
              }
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Workspace</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {workspaceNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    render={
                      <Link to={item.href}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    }
                    isActive={pathname === item.href}
                    tooltip={item.title}
                    className="my-[2px]"
                  />
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {visibleAdminNav.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Administration</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleAdminNav.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      render={
                        <Link to={item.href}>
                          <item.icon />
                          <span>{item.title}</span>
                        </Link>
                      }
                      isActive={pathname === item.href}
                      tooltip={item.title}
                      className="my-[2px]"
                    />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <SidebarMenuButton size="lg">
                    <div className="flex size-8 items-center justify-center rounded-lg bg-muted text-xs font-medium p-2">
                      {getInitials(userName)}
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{userName}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {roleLabel}
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4" />
                  </SidebarMenuButton>
                }
              />
              <DropdownMenuContent side="top" align="start" className="w-56">
                <DropdownMenuItem
                  render={
                    <Link to="/settings?section=profile">
                      <UserRound />
                      Profile
                    </Link>
                  }
                />
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => setSignOutOpen(true)}
                >
                  <LogOut />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <div
              title={`Signed in as ${roleLabel}`}
              className="flex h-8 items-center gap-2 rounded-md px-2 text-sm group-data-[collapsible=icon]:justify-center"
            >
              <ShieldCheck className="size-4 shrink-0" />
              <span className="truncate group-data-[collapsible=icon]:hidden">
                Signed in as {roleLabel}
              </span>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="flex items-center justify-center gap-1 px-2 pt-1 pb-0.5 text-[11px] text-muted-foreground/70 group-data-[collapsible=icon]:hidden">
          <Scale className="size-3" />
          <span>Powered by Clausio</span>
        </div>
      </SidebarFooter>
      <SidebarRail />

      <AlertDialog open={signOutOpen} onOpenChange={setSignOutOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out of Clausio?</AlertDialogTitle>
            <AlertDialogDescription>
              You will need to sign in again before accessing your contract
              workspace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoggingOut}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isLoggingOut}
              onClick={(event) => {
                event.preventDefault();
                void handleLogout();
              }}
            >
              {isLoggingOut ? "Signing out..." : "Yes, sign out"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarRoot>
  );
}
